/**
 * Category List Table Component
 * Table for displaying and managing adventure categories.
 *
 * Rendered inside the tabbed /adventures page, so it owns no <PageShell> — the page owns the
 * title. Columns are data, so the same definitions render as a table at sm+ and as cards below.
 */

import { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Leaderboard as LeaderboardIcon,
} from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { useAdventureCategories, useDeleteCategory } from '../../hooks/useAdventureCategories'
import { CategoryFormDialog } from './CategoryFormDialog'
import type { AdventureCategoryDto } from '@api/types/adventureCategory'

/** Which optional behaviours a category allows, as chips. `null` means "inherit", shown as "X?". */
const getFeatureChips = (category: AdventureCategoryDto) => {
  const chips: { label: string; enabled: boolean | null; variant?: 'filled' | 'outlined' }[] = [
    { label: 'Workflow', enabled: category.workflowAllowed },
    { label: 'Required WF', enabled: category.workflowRequired },
    { label: 'Public', enabled: category.isPublicAllowed, variant: category.isPublicAllowed === null ? 'outlined' : 'filled' },
    { label: 'Groups', enabled: category.groupsAllowed, variant: category.groupsAllowed === null ? 'outlined' : 'filled' },
    { label: 'Repeatable', enabled: category.repeatableAllowed, variant: category.repeatableAllowed === null ? 'outlined' : 'filled' },
  ]

  // Show enabled features, and null values as "inherit" with outlined variant
  return chips.filter((c) => c.enabled === true || c.enabled === null).map((c) => ({
    label: c.enabled === null ? `${c.label}?` : c.label,
    variant: c.variant || 'filled',
  }))
}

export function CategoryListTable() {
  const [showInactive, setShowInactive] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<AdventureCategoryDto | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<AdventureCategoryDto | null>(null)

  // Paging state lives here; DataTable never owns it.
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data: categories, isLoading, error, refetch } = useAdventureCategories(showInactive)
  const deleteMutation = useDeleteCategory()

  const handleCreateClick = () => {
    setSelectedCategory(null)
    setFormDialogOpen(true)
  }

  const handleEditClick = (category: AdventureCategoryDto) => {
    setSelectedCategory(category)
    setFormDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return

    try {
      await deleteMutation.mutateAsync(categoryToDelete.id)
      setCategoryToDelete(null)
    } catch {
      // Error is surfaced by the mutation's own `error` below.
    }
  }

  const columns = useMemo<DataTableColumn<AdventureCategoryDto>[]>(
    () => [
      {
        key: 'icon',
        header: 'Icon/Color',
        width: 110,
        hideBelow: 'md',
        render: (category) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {category.iconName && <Typography variant="h6">{category.iconName}</Typography>}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                backgroundColor: category.color || '#ccc',
                border: '1px solid rgba(0,0,0,0.1)',
              }}
            />
          </Box>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        // The card title on a phone.
        primary: true,
        render: (category) => (
          // Inactive categories stay dimmed, the way the old table dimmed the whole row.
          <Typography variant="body2" fontWeight="medium" sx={{ opacity: category.isActive ? 1 : 0.6 }}>
            {category.name}
          </Typography>
        ),
      },
      {
        key: 'displayName',
        header: 'Display Name',
        render: (category) => (
          <Box sx={{ opacity: category.isActive ? 1 : 0.6 }}>
            <Typography variant="body2">{category.displayName}</Typography>
            {category.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {category.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'features',
        header: 'Features',
        hideBelow: 'lg',
        render: (category) => {
          const chips = getFeatureChips(category)
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                // Card mode right-aligns its value column; the table left-aligns it.
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
              }}
            >
              {category.hasLeaderboard && (
                <Tooltip title="Has Leaderboard">
                  <LeaderboardIcon fontSize="small" color="primary" />
                </Tooltip>
              )}
              {chips.map(({ label, variant }) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  variant={variant as 'filled' | 'outlined'}
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {chips.length === 0 && !category.hasLeaderboard && (
                <Typography variant="caption" color="text.secondary">
                  None
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        key: 'status',
        header: 'Status',
        render: (category) => (
          <Chip
            label={category.isActive ? 'Active' : 'Inactive'}
            color={category.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        key: 'adventureCount',
        header: 'Adventures',
        align: 'right',
        hideBelow: 'md',
        render: (category) => <Typography variant="body2">{category.adventureCount}</Typography>,
      },
      {
        key: 'sortOrder',
        header: 'Sort Order',
        align: 'right',
        width: 110,
        hideBelow: 'md',
        render: (category) => <Typography variant="body2">{category.sortOrder}</Typography>,
      },
    ],
    []
  )

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showInactive}
                onChange={(e) => {
                  setShowInactive(e.target.checked)
                  setPage(0) // the result set changes size, so the current page may not exist
                }}
              />
            }
            label="Show Inactive"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Add Category
        </Button>
      </Box>

      {deleteMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : 'Failed to delete category'}
        </Alert>
      )}

      <DataTable
        rows={categories ?? []}
        columns={columns}
        getRowKey={(category) => String(category.id)}
        loading={isLoading}
        error={error ? (error instanceof Error ? error.message : 'Failed to load categories') : null}
        emptyMessage="No categories found"
        // No `total` => DataTable pages the rows it was given.
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 25, 50]}
        rowActions={(category) => (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEditClick(category)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              {/* span wrapper so the tooltip works while the button is disabled */}
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setCategoryToDelete(category)}
                  disabled={deleteMutation.isPending}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      />

      {/* Form Dialog */}
      <CategoryFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false)
          setSelectedCategory(null)
        }}
        category={selectedCategory}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={Boolean(categoryToDelete)}
        title="Delete Category"
        message={`Are you sure you want to delete the category "${categoryToDelete?.displayName ?? ''}"? This action cannot be undone.`}
        confirmLabel={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onClose={() => setCategoryToDelete(null)}
      />
    </Box>
  )
}
